import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';

type S = components['schemas'];
export type SyllabusItem = S['SyllabusItem'];
export type PlanConfig = S['PlanConfig'];
export type PlanOptions = S['PlanOptions'];
export type PlanPreview = S['PlanPreview'];
export type StudyPlan = S['StudyPlan'];

export const DIAS = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
] as const;
export const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

/** `YYYY-MM-DD` → "lunes 5 de octubre de 2026" (sin zona horaria: es un día de calendario). */
export function fechaDia(d: string, conDia = true): string {
  const [y, m, dd] = d.split('-').map(Number);
  const dow = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1, dd ?? 1)).getUTCDay();
  return `${conDia ? `${(DIAS[dow] ?? '').toLowerCase()} ` : ''}${String(dd)} de ${MESES[(m ?? 1) - 1] ?? ''} de ${String(y)}`;
}

/** Rango legible: "lunes 3 de abril de 2027" o "3 de abril de 2027 – 5 de junio de 2027". */
export function rango(inicio: string, fin: string | null): string {
  return fin ? `${fechaDia(inicio, false)} – ${fechaDia(fin, false)}` : fechaDia(inicio);
}

export const useTemario = () =>
  useQuery({
    queryKey: ['temario'],
    queryFn: () => unwrap<SyllabusItem[]>(api.GET('/api/v1/study-plan/syllabus')),
    staleTime: 10 * 60_000,
  });

export const useOpcionesPlan = () =>
  useQuery({
    queryKey: ['plan', 'opciones'],
    queryFn: () => unwrap<PlanOptions>(api.GET('/api/v1/study-plan/options')),
  });

export const usePlan = () =>
  useQuery({
    queryKey: ['plan'],
    queryFn: () => unwrap<StudyPlan | null>(api.GET('/api/v1/study-plan')),
  });

export function useVistaPrevia() {
  return useMutation({
    mutationFn: (config: PlanConfig) =>
      unwrap<PlanPreview>(api.POST('/api/v1/study-plan/preview', { body: config })),
  });
}

export function useGuardarPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (config: PlanConfig) =>
      unwrap<StudyPlan>(api.PUT('/api/v1/study-plan', { body: config })),
    onSuccess: (plan) => qc.setQueryData(['plan'], plan),
  });
}

/** Descarga el cronograma (PDF con el token en memoria) y lo abre en otra pestaña. */
export async function abrirCronograma(): Promise<void> {
  const { data, response } = await api.GET('/api/v1/study-plan/schedule.pdf', { parseAs: 'blob' });
  if (!response.ok || !data)
    throw new Error('No pudimos generar tu cronograma. Inténtalo de nuevo.');
  const url = URL.createObjectURL(data);
  const win = window.open(url, '_blank', 'noopener');
  if (!win) {
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Consalud ENARM - Cronograma de estudio.pdf';
    a.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export type EventosCalendario = S['CalendarEvents'];
export type EventoPersonal = S['PersonalEvent'];
export type EventoEnarm = S['EnarmCalendarEvent'];
export type EventoPersonalBody = S['PersonalEventBody'];

/** Colores de los eventos personales (los mismos que acepta la API). */
export const COLORES_EVENTO: Record<EventoPersonal['color'], { nombre: string; hex: string }> = {
  morado: { nombre: 'Morado', hex: '#720294' },
  azul: { nombre: 'Azul', hex: '#005DB9' },
  verde: { nombre: 'Verde', hex: '#6BBF49' },
  cian: { nombre: 'Cian', hex: '#02D1D5' },
  naranja: { nombre: 'Naranja', hex: '#F59E0B' },
  rojo: { nombre: 'Rojo', hex: '#E5484D' },
};

/** Eventos personales (aparte del plan: reconfigurarlo no los borra) y etapas ENARM. */
export const useEventos = () =>
  useQuery({
    queryKey: ['plan', 'eventos'],
    queryFn: () => unwrap<EventosCalendario>(api.GET('/api/v1/study-plan/events')),
  });

export function useGuardarEvento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: EventoPersonalBody }) =>
      id === undefined
        ? unwrap<EventoPersonal>(api.POST('/api/v1/study-plan/events', { body }))
        : unwrap<EventoPersonal>(
            api.PATCH('/api/v1/study-plan/events/{id}', { params: { path: { id } }, body }),
          ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan', 'eventos'] }),
  });
}

export function useBorrarEvento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { response } = await api.DELETE('/api/v1/study-plan/events/{id}', {
        params: { path: { id } },
      });
      if (!response.ok) await unwrap(Promise.resolve({ response }) as never);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan', 'eventos'] }),
  });
}
